import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, CheckCircle2, Loader2, ArrowRight, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const formatNext = (n) => {
  if (!n || !n.session_date) return null;
  try {
    // The session_date/session_time are already in the specified timezone — format directly, no conversion.
    const [y, m, d] = n.session_date.split("-").map(Number);
    const [hh, mm] = n.session_time.split(":").map(Number);
    // Weekday: build a UTC date at that clock time to compute weekday (weekday name is tz-independent for a given date)
    const weekday = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = ((hh + 11) % 12) + 1;
    const mins = mm.toString().padStart(2, "0");
    // Short TZ abbreviation (EDT vs EST) for the actual date
    const tz = n.timezone || "America/New_York";
    let tzShort = "ET";
    try {
      tzShort = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" })
        .formatToParts(new Date(Date.UTC(y, m - 1, d, 12)))
        .find((p) => p.type === "timeZoneName")?.value || "ET";
    } catch {}
    return `${weekday}, ${MONTHS[m - 1]} ${d} · ${h12}:${mins} ${period} ${tzShort}`;
  } catch {
    return null;
  }
};

const Community = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [next, setNext] = useState(null);

  useEffect(() => {
    api.get("/connecting-grounds/next").then((r) => {
      if (r.data?.scheduled) setNext(r.data);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim()) {
      toast.error("Just your name and email — that's all we need.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/connecting-grounds", { name: name.trim(), email: email.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went sideways — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[#F6F1EA]" data-testid="community-page">
      <section className="pt-32 pb-16 sm:pt-40 border-b border-[#D7B2A3]/30">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#D7B2A3]/40 flex items-center justify-center mx-auto mb-8">
            <Users className="text-[#B89B63]" size={26} />
          </div>
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-6">Community · Free</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4F5D4F] leading-[1.08] tracking-tight" data-testid="community-heading">
            You weren't meant to <em className="italic text-[#B89B63]">do this alone</em>.
          </h1>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="max-w-4xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-4">
              Connecting Grounds
            </h2>
            <p className="text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-6">
              A free monthly virtual gathering for women questioning and rebuilding after
              high-control Christianity.
            </p>
            <ul className="space-y-3 text-[#4F5D4F]/85 font-light mb-8">
              <li className="flex items-baseline gap-3"><span className="text-[#B89B63]">·</span> No theological debates.</li>
              <li className="flex items-baseline gap-3"><span className="text-[#B89B63]">·</span> No pressure to have the right answer.</li>
              <li className="flex items-baseline gap-3"><span className="text-[#B89B63]">·</span> No requirement to know where you'll land.</li>
              <li className="flex items-baseline gap-3"><span className="text-[#B89B63]">·</span> Just a place to be around women who understand why this can be so complicated.</li>
            </ul>

            <div className="bg-white border border-[#D7B2A3]/60 p-6" data-testid="community-next-card">
              {next ? (
                <>
                  <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3 flex items-center gap-2">
                    <Calendar size={12} /> Our next gathering
                  </p>
                  <p className="font-serif text-2xl text-[#4F5D4F] leading-snug" data-testid="community-next-date">
                    {formatNext(next) || "The 2nd Friday of every month"}
                  </p>
                  <p className="text-[#4F5D4F]/80 font-light mt-2">
                    On Google Meet. I'll send you the link with a reminder before we gather.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">When we meet</p>
                  <p className="font-serif text-2xl text-[#4F5D4F] leading-snug">
                    The 2nd Friday of every month.
                  </p>
                  <p className="text-[#4F5D4F]/80 font-light mt-2">On Google Meet.</p>
                </>
              )}
            </div>
          </div>

          <div className="md:col-span-6">
            {done ? (
              <div className="bg-[#4F5D4F] text-[#F6F1EA] p-10" data-testid="community-success">
                <CheckCircle2 className="text-[#D7B2A3] mb-6" size={40} />
                <h3 className="font-serif text-3xl mb-3">You're in.</h3>
                <p className="text-[#F6F1EA]/85 font-light leading-relaxed mb-6">
                  I just sent a warm welcome to <strong>{email}</strong>. A few days before each
                  gathering, I'll email you the Google Meet link.
                </p>
                <p className="text-[#F6F1EA]/70 font-light italic text-sm mb-8">
                  In the meantime, be gentle with yourself.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-3 border border-[#F6F1EA] text-[#F6F1EA] hover:bg-[#F6F1EA] hover:text-[#4F5D4F] px-6 py-3 text-xs tracking-eyebrow uppercase transition-colors"
                  data-testid="community-return-home"
                >
                  Return home <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="bg-white border border-[#D7B2A3]/60 p-8 sm:p-10"
                data-testid="community-form"
              >
                <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">Claim your seat</p>
                <h3 className="font-serif text-2xl text-[#4F5D4F] mb-6 leading-tight">
                  Join Connecting Grounds
                </h3>

                <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Your name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name is fine"
                  className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 h-11 mb-5"
                  data-testid="community-name"
                />

                <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.com"
                  className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 h-11 mb-8"
                  data-testid="community-email"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex w-full items-center justify-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] disabled:opacity-40 disabled:cursor-not-allowed px-7 py-4 text-xs tracking-eyebrow uppercase transition-colors"
                  data-testid="community-submit"
                >
                  {submitting ? <><Loader2 className="animate-spin" size={14} /> Saving your seat…</> : <>Join Connecting Grounds <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></>}
                </button>

                <p className="text-xs text-[#8C9A88] italic text-center mt-5">
                  I'll only email you about Connecting Grounds. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Community;
